import React, { Component } from "react";
import { Theme, withStyles } from "@material-ui/core";
import { WithTranslation, withTranslation } from "react-i18next";
import createStyles from "@material-ui/core/styles/createStyles";
import { COLORS } from "../../styles/colors";
import AddIcon from "@material-ui/icons/Add";
import Fab from "@material-ui/core/Fab";
import { RouteComponentProps } from "react-router";
import List from "../List";
import LocationStore, { Location } from "../../stores/locationStore";
import { inject, observer } from "mobx-react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import { CheckboxWithLabel, TextField } from "formik-material-ui";
import { Field, Formik, FormikActions } from "formik";
import * as Yup from "yup";
import { StringValidator } from "../../utils/validators";
import TableCell from "@material-ui/core/TableCell";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import TableRow from "@material-ui/core/TableRow";
import { DialogComponent } from "../DialogComponent";
import Filters from "../Filters";
import { InjectedNotistackProps, withSnackbar } from "notistack";
import Divider from "@material-ui/core/Divider";
import classNames from "classnames";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import AppStore from "../../stores/appStore";

interface MatchParams {
  id: string;
}

interface LocationListProps extends WithTranslation, InjectedNotistackProps {}

interface InjectedProps
  extends LocationListProps,
    RouteComponentProps<MatchParams> {
  locationStore: LocationStore;
  appStore: AppStore;
  classes: any;
}

interface LocationListState {
  open: boolean;
  edit: string;
  add: boolean;
  openDeleteDialog: string;
  filters: {
    [key: string]: string;
  };
  sortParams: {
    [key: string]: string;
  };
}

class LocationForm {
  constructor(location?: Location) {
    if (location) {
      this.name = location.name;
      this.active = location.active;
    }
  }

  name: string = "";
  active: boolean = true;
}

const LocationSchema = Yup.object().shape<LocationForm>({
  name: StringValidator,
  active: Yup.boolean()
});

@inject("locationStore", "appStore")
@observer
class LocationList extends Component<LocationListProps, LocationListState> {
  get p() {
    return this.props as InjectedProps;
  }

  state = {
    open: false,
    add: false,
    edit: "",
    openDeleteDialog: "",
    filters: {},
    sortParams: {}
  };

  filtersList = [
    {
      name: "name",
      label: this.p.t("LOCATION_LIST.NAME"),
      type: "input"
    }
  ];

  headers = [
    {
      name: this.p.t("LOCATION_LIST.NAME"),
      key: "name",
      sort: true
    },
    {
      name: this.p.t("LOCATION_LIST.ACTIONS"),
      key: "actions"
    }
  ];

  getLocationList = (sortParams: any) => {
    if (Object.keys(sortParams).length) {
      this.setState({ sortParams });
    }
    const sort = { ...this.state.sortParams, ...sortParams };
    const params = {
      ...sort,
      ...this.state.filters
    };
    this.p.locationStore.getLocationListAction(params);
  };

  handleAddLocation = () => {
    this.setState({ add: true, edit: "", open: true });
  };
  handleClose = () => {
    this.setState({ openDeleteDialog: "", edit: "", add: false, open: false });
  };

  handleOpenLocation = (uuid: string) => () => {
    this.setState({ edit: uuid });
  };

  handleDeleteLocation = (uuid: string) => () => {
    this.setState({ openDeleteDialog: uuid });
  };

  submit = async (values: LocationForm) => {
    if (this.state.edit) {
      this.editLocation(values);
    } else {
      this.addLocation(values);
    }
  };

  addLocation = async (values: LocationForm) => {
    const success = await this.p.locationStore.addLocationAction(values);
    if (!success) {
      this.p.enqueueSnackbar(this.p.t("ERRORS.CANT_ADD_LOCATION"), {
        variant: "error"
      });
    } else {
      this.p.enqueueSnackbar(this.p.t("LOCATION_LIST.ADD_SUCCESS"), {
        variant: "success"
      });
      this.p.history.replace("/locations");
    }
    this.handleClose();
  };

  deleteLocation = async () => {
    const id = this.state.openDeleteDialog;
    const success = await this.p.locationStore.deleteLocationAction(id);

    if (!success) {
      this.props.enqueueSnackbar(this.p.t("ERRORS.CANT_DELETE_LOCATION"), {
        variant: "error"
      });
    } else {
      this.props.enqueueSnackbar(this.p.t("LOCATION_LIST.DELETE_SUCCESS"), {
        variant: "success"
      });
      this.p.history.replace("/locations");
    }
    this.handleClose();
    this.getLocationList({});
  };

  editLocation = async (values: LocationForm) => {
    const id = this.state.edit;
    const success = await this.p.locationStore.editLocationAction(values, id);

    if (!success) {
      this.props.enqueueSnackbar(this.p.t("ERRORS.CANT_EDIT_LOCATION"), {
        variant: "error"
      });
    } else {
      this.props.enqueueSnackbar(this.p.t("LOCATION_LIST.UPDATE_SUCCESS"), {
        variant: "success"
      });
      this.p.history.replace("/locations");
    }
    this.handleClose();
    this.getLocationList({});
  };

  handleChange = (filters: any) => {
    this.setState({ filters }, () => this.getLocationList({}));
  };

  render() {
    const {
      classes,
      t,
      locationStore: { locationList, locationsTotalCount, pending },
      appStore: { isFilter }
    } = this.p;
    return (
      <div className={classes.listContainer}>
        <Typography
          className={classNames(classes.dividerFullWidth, "title")}
          color="textSecondary"
          display="block"
          variant="subtitle1"
        >
          {t("LOCATION_LIST.TITLE")}
        </Typography>
        <Divider component="div" className={"divider"} />
        <Filters handleFilter={this.handleChange} filters={this.filtersList} />
        <List
          data={locationList}
          getData={this.getLocationList}
          headers={this.headers}
          isFilter={isFilter}
          totalCount={locationsTotalCount}
          headerClass={classes.headerPadding}
        >
          {locationList &&
            locationList.length &&
            locationList.map((item: any, i: number) => (
              <TableRow key={item.uuid || item.id || i} className={"rowList"}>
                <TableCell className={classes.tableCell}>{item.name}</TableCell>
                <TableCell className={classes.cellActions}>
                  <EditIcon
                    onClick={this.handleOpenLocation(item.id)}
                    className={classes.iconClick}
                  />
                  <DeleteIcon
                    className={classNames(
                      classes.iconMargin,
                      classes.iconClick
                    )}
                    onClick={this.handleDeleteLocation(item.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
        </List>
        <Tooltip title="Add" placement="top-start">
          <Fab
            color="primary"
            aria-label="Add"
            className={classNames(classes.fab, classes.addButton)}
            onClick={this.handleAddLocation}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
        <Dialog
          open={!!this.state.edit || this.state.open}
          onClose={this.handleClose}
          fullWidth={true}
          disableBackdropClick={true}
          PaperProps={{
            classes: {
              root: classes.locationDialog
            }
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <Formik
            enableReinitialize
            initialValues={
              new LocationForm(
                locationList.find(item => item.id === this.state.edit)
              )
            }
            validationSchema={LocationSchema}
            onSubmit={(
              values: LocationForm,
              actions: FormikActions<LocationForm>
            ) => {
              this.submit(values).then(() => {
                actions.setSubmitting(false);
              });
            }}
            onReset={values => {
              values = new LocationForm();
            }}
          >
            {props => {
              return (
                <>
                  <DialogTitle>
                    {!!this.state.add && t("LOCATION_LIST.ADD_LOCATION")}{" "}
                    {!!this.state.edit && t("LOCATION_LIST.EDIT_LOCATION")}
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText id="alert-dialog-description" />
                    <Field
                      id="name"
                      name="name"
                      label={t("LOCATION_LIST.NAME")}
                      required
                      InputProps={{
                        classes: {
                          notchedOutline: classes.notchedOutline
                        }
                      }}
                      component={TextField}
                      className={classNames(
                        classes.textField,
                        classes.formControl
                      )}
                      margin="normal"
                      autoComplete="off"
                      variant="outlined"
                    />
                    <div>
                      <Field
                        name="active"
                        Label={{ label: t("LOCATION_LIST.ACTIVE") }}
                        component={CheckboxWithLabel}
                      />
                    </div>
                  </DialogContent>
                  <DialogActions className={classes.btnLocationDialog}>
                    <Button
                      onClick={props.submitForm}
                      color="primary"
                      variant="contained"
                      className={classes.btn}
                    >
                      {t("SAVE")}
                    </Button>
                    <Button
                      onClick={this.handleClose}
                      color="primary"
                      variant="contained"
                      className={classes.btn}
                    >
                      {t("CANCEL")}
                    </Button>
                  </DialogActions>
                </>
              );
            }}
          </Formik>
        </Dialog>
        <DialogComponent
          title={"LOCATION_LIST.LOCATION_DELETE"}
          t={t}
          classes={classes}
          submitButtonTitle={"DELETE"}
          cancel={"CANCEL"}
          pending={pending}
          handleClickCloseDialog={this.handleClose}
          handleDelete={this.deleteLocation}
          open={!!this.state.openDeleteDialog}
        />
      </div>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    listContainer: {
      maxWidth: "100%",
      position: "relative",
      height: "calc(100vh - 90px)"
    },

    addButton: {
      color: "#fff",
      position: "fixed",
      backgroundColor: COLORS.PRIMARY_DARK,
      bottom: 20,
      right: 30
    },

    notchedOutline: {
      borderWidth: 1,
      borderColor: COLORS.WHITE + "!important"
    },

    formControl: {
      background: "rgba(255, 255, 255, 0.12)",
      width: "70%"
    },

    btn: {
      color: COLORS.WHITE,
      background: COLORS.PRIMARY_DARK,
      width: "20%"
    },

    locationDialog: {
      textAlign: "center",
      background: COLORS.DIALOG_COLOR,
      borderRadius: 10
    },

    btnLocationDialog: {
      justifyContent: "center"
    },

    cellActions: {
      maxWidth: 100,
      padding: "16px 5px",
      textAlign: "left",
      width: 100
    },

    iconMargin: {
      marginLeft: 20
    },

    btnColor: {
      color: COLORS.WHITE,
      "&:hover": {
        background: COLORS.PRIMARY_DARK
      }
    },
    iconClick: {
      cursor: "pointer"
    },

    headerPadding: {
      padding: "10px 20px 10px 10px;",
      textAlign: "left"
    }
  });

export default withStyles(styles)(
  withTranslation()(withSnackbar(LocationList))
);
